"use client";

/**
 * AdminTabs — 탭으로 콘텐츠를 전환하는 공통 컴포넌트
 *
 * 탭 목록과 각 탭의 콘텐츠를 items 로 한번에 받는다. 상세보기 모달/드로어 안에서
 * "기본 정보 / 변경 이력" 같이 영역을 나눌 때 AdminModal, AdminDrawer 등의 children 으로 바로 넣으면 된다.
 * value 를 주면 제어 컴포넌트, 안 주면 비제어(내부 상태)로 동작한다.
 *
 * type 으로 스타일을 고른다 (기본 "line" — 밑줄 탭, antd 기본 스타일).
 * "segment"를 주면 알약형 배경 탭(shadcn 기본 스타일)이 된다.
 *
 * @example
 * <AdminTabs
 *   items={[
 *     { value: "info", label: "기본 정보", content: <AdminDescriptions items={items} /> },
 *     { value: "history", label: "변경 이력", content: <HistoryTable /> },
 *   ]}
 * />
 * <AdminTabs type="segment" items={[...]} />
 */
import type { ReactNode } from "react";

import { cn } from "../../lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger, type TabsVariant } from "../ui/tabs";

export interface AdminTabItem {
  value: string;
  label: ReactNode;
  content: ReactNode;
  disabled?: boolean;
}

export interface AdminTabsProps {
  items: AdminTabItem[];
  /** 탭 스타일 (기본 "line") */
  type?: TabsVariant;
  /** 선택된 탭 값 (제어 컴포넌트로 쓸 때) */
  value?: string;
  /** 초기 선택 탭 값 (비제어). 안 주면 첫 번째 탭 */
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  listClassName?: string;
  contentClassName?: string;
}

export function AdminTabs({
  items,
  type = "line",
  value,
  defaultValue,
  onValueChange,
  className,
  listClassName,
  contentClassName,
}: AdminTabsProps) {
  return (
    <Tabs
      variant={type}
      value={value}
      defaultValue={defaultValue ?? items[0]?.value}
      onValueChange={onValueChange}
      className={className}
    >
      <TabsList className={cn("w-full", listClassName)}>
        {items.map((item) => (
          <TabsTrigger key={item.value} value={item.value} disabled={item.disabled}>
            {item.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {items.map((item) => (
        <TabsContent
          key={item.value}
          value={item.value}
          className={contentClassName}
        >
          {item.content}
        </TabsContent>
      ))}
    </Tabs>
  );
}
